import React, { useState } from "react";
import { useLatest } from "../hooks";
import { locationServiceOptions } from "../services/api";
import * as f from "../utils/formatter";
import AutocompleteSelect from "./AutocompleteSelect";
import { selectKeys } from "shared/src/util.mjs";

function useServiceFilter({ location_id, product_id, truthyFilters = [] }) {
  return React.useMemo(() => {
    return location_id == null || product_id == null
      ? null
      : {
          location_id: { eq: location_id },
          product_id: { eq: product_id },
          ...truthyFilters.reduce((acc, f) => {
            acc[f] = { eq: true };

            return acc;
          }, {}),
        };
  }, [location_id, product_id]);
}

/**
 * @param {AbortSignal} signal
 */
const fetchServiceOptions = async (signal, filter) => {
  if (filter == null) {
    return [];
  }

  const res = await locationServiceOptions(
    {
      filter: filter,
    },
    { signal },
  );

  return res.data.locationServices.data.map((ls) => ({
    id: ls.service.id,
    duration: ls.duration,
    name: `${ls.service.name} (${f.minutesToDuration(ls.duration)})`,
    requires_doctor: ls.requires_doctor,
  }));
};

function useServiceOptions(filter) {
  const [options, setOptions] = useState();
  const result = React.useMemo(
    () => ({ options: options ?? [], loading: options == null }),
    [options],
  );

  useLatest(
    async (signal) => {
      if (filter == null) {
        setOptions([]);
        return;
      }

      setOptions(null);
      setOptions(await fetchServiceOptions(signal, filter));
    },
    [filter],
  );

  return result;
}

/**
 * @typedef {Pick<import('./AutocompleteSelect').AutocompleteSelectProps, 'required' | 'name'>} _AutocompleteSelectProps
 *
 * @typedef _ServiceSelectorProps
 * @property {string} props.location_id
 * @property {string} props.product_id
 * @property {('create_reservation' | 'change_reservation_to')[]} [props.truthyFilters]
 * @property {string} [props.default_service_id]
 *
 * @typedef {_AutocompleteSelectProps & _ServiceSelectorProps} ServiceSelectorProps
 *
 * @param {ServiceSelectorProps} props
 */
export function ServiceSelector(props) {
  const filter = useServiceFilter(props);
  const options = useServiceOptions(filter);

  const autocompleteSelectProps = selectKeys(props, ["required", "name"]);

  const defaultValue =
    props.default_service_id == null
      ? null
      : options.options.find((o) => o.id === props.default_service_id);

  return (
    <AutocompleteSelect
      key={options.loading ? "0" : "1"}
      loading={options.loading}
      options={options.options}
      defaultValue={defaultValue}
      label="Plánovaný úkon"
      {...autocompleteSelectProps}
    />
  );
}
